import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckboxMultiSelect } from "@/components/custom/CheckboxMultiSelect";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  useCreateCurrentAssessmentMutation,
  useGetPatientAssessmentDetailQuery,
} from "../../infrastructure/api/patientsApi";
import {
  useGetDoctorsQuery,
  useGetGroupsQuery,
} from "@/features/members-groups/infrastructure/api/membersGroupsApi";
import { useParams } from "react-router-dom";
import {
  formatDateTimeShort,
  getMinimumFutureTimeInputValue,
  isPastOrCurrentDateTime,
  isSameLocalDate,
} from "@/lib/dateUtils";
import { TimeEntryField } from "./scheduling/TimeEntryField";
import { useDoctorAvailabilityCheck } from "./scheduling/doctorAvailability";
import {
  currentTimeEntry,
  timeEntryFrom24h,
  timeEntryToDate,
  type TimeEntry,
} from "./scheduling/timeEntry";

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: (assessmentId: string) => void;
  assessmentId: string;
  currentStatus: string;
  /** ISO string of the currently scheduled start — new time must differ */
  currentScheduledStart?: string | null;
  /** Schedule a brand-new assessment (CC_SCHEDULED) when no ongoing assessment exists */
  createMode?: boolean;
};

const getDefaultAppointmentDuration = (status: string): number => {
  if (status.startsWith("CC_")) return 30;
  return 45;
};

const getAppointmentLabel = (status: string, t: TFunction<"patients">): string => {
  if (status.startsWith("CC_")) return t("rescheduleDialog.labelClinicalExamination");
  if (status.startsWith("RR_")) return t("rescheduleDialog.labelReportReview");
  return t("rescheduleDialog.labelScreening");
};

const RescheduleScreeningDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  assessmentId,
  currentStatus,
  currentScheduledStart,
  createMode = false,
}) => {
  const { t } = useTranslation("patients");
  const days = [
    t("scheduling.days.su"),
    t("scheduling.days.mo"),
    t("scheduling.days.tu"),
    t("scheduling.days.we"),
    t("scheduling.days.th"),
    t("scheduling.days.fr"),
    t("scheduling.days.sa"),
  ];
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(
    new Date(today.getFullYear(), today.getMonth()),
  );
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [doctors, setDoctors] = useState<string[]>([]);
  const [doctorError, setDoctorError] = useState("");
  const defaultDuration = getDefaultAppointmentDuration(currentStatus);
  const [time, setTime] = useState<TimeEntry>(currentTimeEntry);

  const { patientId } = useParams<{ patientId: string }>();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();

  const { data, isLoading } = useGetPatientAssessmentDetailQuery(
    { patientId: patientId as string, assessmentId },
    { skip: !patientId || !assessmentId },
  );
  const shouldFetchDoctors = !currentStatus.startsWith("SCR_");
  const { data: groups = [], isLoading: isLoadingGroups } = useGetGroupsQuery(
    undefined,
    { skip: !shouldFetchDoctors },
  );
  const groupIds = groups.map((group) => group.id);
  const { data: doctorMembers = [], isLoading: isLoadingDoctors } =
    useGetDoctorsQuery(
      { group_ids: groupIds },
      { skip: !shouldFetchDoctors || groupIds.length === 0 },
    );

  const doctorOptions = doctorMembers.map((member) => ({
    id: member.id,
    name: member.name,
    phone: member.phone,
    designation: member.role || null,
  }));

  const recommendedDoctors = shouldFetchDoctors
    ? doctorOptions
    : currentStatus.startsWith("SCR_")
      ? (data?.data?.assessment?.screening_recommended_doctors ??
        data?.data?.assessment?.clinical_context_recommended_doctors ??
        [])
      : (data?.data?.assessment?.clinical_context_recommended_doctors ?? []);

  const [createAssessment, { isLoading: isSubmitting }] =
    useCreateCurrentAssessmentMutation();
  const { isSlotAvailable, isChecking } = useDoctorAvailabilityCheck();

  // ✅ ONLY CHANGE: added this helper
  const getRescheduleStatus = (status: string): string | null => {
    if (status.startsWith("CC_")) return "CC_RESCHEDULED";
    if (status.startsWith("SCR_")) return "SCR_RESCHEDULED";
    if (status.startsWith("RR_")) return "RR_RESCHEDULED";
    return null;
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1));
    setSelectedDate(null);
  };

  const handleReschedule = async () => {
    const rescheduleStatus = createMode
      ? "CC_SCHEDULED"
      : getRescheduleStatus(currentStatus);

    if (!rescheduleStatus) {
      return toast.error(
        t("rescheduleDialog.invalidStatus", { status: currentStatus }),
      );
    }

    if (doctors.length === 0) {
      setDoctorError(t("scheduling.selectDoctorError"));
      return;
    }
    if (!selectedDate) return toast.error(t("scheduling.selectDateError"));

    const startDate = timeEntryToDate(year, month, selectedDate, time);
    if (!startDate) {
      return toast.error(t("scheduling.invalidTimeError"));
    }

    if (isPastOrCurrentDateTime(startDate)) {
      return toast.error(t("scheduling.futureTimeError"));
    }

    // The API requires an end time; appointments get the default duration.
    const endDate = new Date(startDate.getTime() + defaultDuration * 60_000);

    const available = await isSlotAvailable({
      doctorIds: doctors,
      start: startDate,
      end: endDate,
      excludeAssessmentId: assessmentId,
    });
    if (!available) {
      return toast.error(t("scheduling.doctorUnavailableError"));
    }

    try {
      const payload: {
        scheduled_start_at: string;
        scheduled_end_at: string;
        status: string;
        recommended_clinical_context_doctor_user_ids?: string[];
        recommended_screening_doctor_user_ids?: string[];
        recommended_report_review_doctor_user_ids?: string[];
      } = {
        scheduled_start_at: startDate.toISOString(),
        scheduled_end_at: endDate.toISOString(),
        status: rescheduleStatus,
      };

      if (currentStatus.startsWith("RR_")) {
        payload.recommended_report_review_doctor_user_ids = doctors;
      } else if (currentStatus.startsWith("SCR_")) {
        payload.recommended_screening_doctor_user_ids = doctors;
      } else {
        payload.recommended_clinical_context_doctor_user_ids = doctors;
      }

      const result = await createAssessment({
        patientId: patientId as string,
        data: payload,
      }).unwrap();

      toast.success(
        createMode
          ? t("rescheduleDialog.createSuccess")
          : t("rescheduleDialog.rescheduleSuccess"),
      );
      onSuccess?.(result.id);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(
        apiErrorMessage(
          err,
          createMode
            ? t("rescheduleDialog.createError")
            : t("rescheduleDialog.rescheduleError"),
        ),
      );
    }
  };
  const renderCalendar = () => {
    const cells: React.ReactNode[] = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} />);
    }

    const todayDate =
      year === today.getFullYear() && month === today.getMonth()
        ? today.getDate()
        : null;

    // Highlight the currently scheduled date (if in same month/year)
    let scheduledDay: number | null = null;
    if (currentScheduledStart) {
      const s = new Date(currentScheduledStart);
      if (s.getFullYear() === year && s.getMonth() === month) {
        scheduledDay = s.getDate();
      }
    }

    for (let d = 1; d <= totalDays; d++) {
      const isSelected = selectedDate === d;
      const isToday = todayDate === d;
      const isCurrentlyScheduled = scheduledDay === d;
      const isPast = new Date(year, month, d).setHours(23, 59, 59) < Date.now();

      cells.push(
        <button
          key={d}
          onClick={() => {
            if (isPast) return;
            setSelectedDate(d);

            const selected = new Date(year, month, d);
            const start = timeEntryToDate(year, month, d, time);
            if (isSameLocalDate(selected) && start && isPastOrCurrentDateTime(start)) {
              setTime(timeEntryFrom24h(getMinimumFutureTimeInputValue()));
            }
          }}
          disabled={isPast}
          title={isCurrentlyScheduled ? t("scheduling.currentlyScheduledDate") : undefined}
          className={`h-10 rounded-md text-sm relative ${
            isPast
              ? "text-gray-300 cursor-not-allowed"
              : isSelected
                ? "bg-primary text-white"
                : isCurrentlyScheduled
                  ? "border border-primary text-primary"
                  : isToday
                    ? "font-semibold"
                    : "hover:bg-gray-100"
          }`}
        >
          {d}
          {isCurrentlyScheduled && !isSelected && (
            <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
          )}
        </button>,
      );
    }

    return cells;
  };

  const scheduledLabel = formatDateTimeShort(currentScheduledStart);
  const appointmentLabel = getAppointmentLabel(currentStatus, t);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl p-0 sm:max-w-[650px]">
        {/* Header */}
        <div className="shrink-0 border-b px-6 pt-5 pb-3">
          <h2 className="text-lg font-semibold">
            {createMode
              ? t("rescheduleDialog.createTitle")
              : t("rescheduleDialog.rescheduleTitle", { label: appointmentLabel })}
          </h2>
          <p className="text-sm text-gray-500">
            {createMode
              ? t("rescheduleDialog.createSubtitle")
              : currentScheduledStart
                ? t("rescheduleDialog.currentlyScheduledSubtitle", { date: scheduledLabel })
                : t("rescheduleDialog.selectNewSubtitle", { label: appointmentLabel })}
          </p>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-4">
          {/* Calendar */}
          <div>
            <div className="flex justify-between mb-3">
              <p className="font-medium">
                {currentDate.toLocaleString("default", { month: "short" })}{" "}
                {year}
              </p>
              <div className="flex gap-2">
                <button onClick={handlePrevMonth}>
                  <ChevronLeft size={16} className="cursor-pointer" />
                </button>
                <button onClick={handleNextMonth}>
                  <ChevronRight size={16} className="cursor-pointer" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 text-xs text-gray-500 text-center mb-2">
              {days.map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 text-center">
              {renderCalendar()}
            </div>
          </div>

          <TimeEntryField id="reschedule-time" value={time} onChange={setTime} />

          {/* Doctor */}
          <div>
            <label className="text-sm font-medium mb-1 block">
              {t("scheduling.doctorName")} *
            </label>
            <CheckboxMultiSelect
              id="reschedule-doctors"
              options={recommendedDoctors.map((doc) => ({
                id: doc.id,
                label: doc.name,
                detail: doc.designation,
              }))}
              value={doctors}
              onChange={(ids) => {
                setDoctors(ids);
                setDoctorError("");
              }}
              placeholder={t("scheduling.selectDoctor")}
              selectedText={(count) => t("scheduling.doctorsSelected", { count })}
              emptyText={t("scheduling.noDoctors")}
              loading={isLoading || isLoadingGroups || isLoadingDoctors}
              loadingText={t("scheduling.loading")}
              invalid={!!doctorError}
              removeLabel={(name) => t("scheduling.removeDoctor", { name })}
              triggerClassName="h-12"
            />
            {doctorError && (
              <p className="text-xs text-destructive-text mt-1">
                {doctorError}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 justify-end gap-2 border-t px-6 py-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="cursor-pointer px-3 hover:bg-white"
            disabled={isSubmitting}
          >
            {t("scheduling.cancel")}
          </Button>
          <Button
            onClick={handleReschedule}
            loading={isSubmitting || isChecking}
            className="cursor-pointer bg-primary px-3 text-white"
          >
            {createMode ? t("rescheduleDialog.createButton") : t("rescheduleDialog.rescheduleButton")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleScreeningDialog;
